import React from 'react';
import { motion } from 'framer-motion';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const Button3D: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  className = '',
  ...props 
}) => {
  const baseClasses = "font-heading font-bold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 active:scale-95 relative overflow-hidden";
  
  const variants = {
    primary: "bg-gradient-to-br from-be-france to-be-night text-white shadow-lg shadow-blue-600/40 hover:shadow-xl hover:shadow-blue-600/60 hover:brightness-110",
    secondary: "bg-gradient-to-br from-be-sky to-be-france text-white shadow-lg shadow-sky-400/40 hover:shadow-xl hover:shadow-sky-400/60 hover:brightness-110",
    outline: "bg-transparent border-2 border-be-france text-be-france dark:text-white dark:border-white shadow-md shadow-gray-200/50 dark:shadow-none hover:bg-blue-50/50 dark:hover:bg-white/10"
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-5 py-2 text-sm",
    lg: "px-6 py-2.5 text-base"
  };

  return (
    <button 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const SectionTitle: React.FC<{ title: string; subtitle?: string; align?: 'left' | 'center' }> = ({ title, subtitle, align = 'center' }) => {
  return (
    <div className={`mb-8 ${align === 'center' ? 'text-center' : 'text-left'}`}>
      <motion.div 
        initial={{ width: 0 }}
        whileInView={{ width: 60 }}
        viewport={{ once: true }}
        className={`h-1.5 bg-gradient-to-r from-be-night via-be-france to-be-sky rounded-full mb-3 ${align === 'center' ? 'mx-auto' : ''}`}
      />
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-be-darkest dark:text-white mb-3 uppercase tracking-wider">
        {title}
      </h2>
      {subtitle && (
        <p className="text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
};

export const Card3D: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  return (
    <div className={`relative group ${className}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-be-night to-be-france opacity-80 rounded-xl translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-200 blur-sm"></div>
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 h-full shadow-xl transform transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:-translate-x-0.5">
            {children}
        </div>
    </div>
  );
}